import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { compact } from 'lodash'
import type moment from 'moment'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import enableDay from 'modules/contracts/lib/enableDay'
import { useContractVersionsPayrollPolicies } from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import Compensation from 'modules/contracts/store/models/Compensation'
import StartsOnDatePicker from 'modules/payroll_bundle/pages/settings/Components/PayrollPolicy/StartsOnDatePicker'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'
import Policy from 'modules/payroll_bundle/store/models/Policy'

interface Props {
  compensation: Compensation
  isEditable: boolean
  form: Form
}

const SupplementsSettings = ({ compensation, isEditable, form }: Props) => {
  const policies = usePolicies()
  const contractVersionsPayrollPolicies = useContractVersionsPayrollPolicies()

  const policiesOptionsFromContractVersion: SelectOption[] = compact(
    // Only the policies associated to the contract should be selectable
    contractVersionsPayrollPolicies
      .filter({ contract_version_id: Number(compensation.attributes.contract_version_id) })
      .map((cvpp) => {
        const policy = policies.get(cvpp.attributes.payroll_policy_id)

        if (!policy) return null

        return {
          label: policy.attributes.name,
          value: policy.id,
        }
      })
  )

  const policiesOptionsFromCompensation = () => {
    if (compensation.attributes.payroll_policy_id == null) return [null]
    const policy = policies.get(Number(compensation.attributes.payroll_policy_id))

    if (!policy) return [null]

    return [
      {
        label: policy.attributes.name,
        value: policy.id,
      },
    ]
  }

  const policiesOptionsFromCompensationValue = policiesOptionsFromCompensation()

  const policiesOptions = compensation.attributes.contract_version_id
    ? policiesOptionsFromContractVersion
    : (policiesOptionsFromCompensationValue as SelectOption[])

  // If the compensation is not linked to a payroll policy, it sets the policy
  // field so that the first policy linked to the contract is pre-selected in
  // the "Payroll policy" dropdown.
  const policyField = form.get('payroll_policy_id')
  const isPerWorkedDay = form.get('compensation_type').value === Compensation.types.per_worked_day

  if (!policyField.value) {
    policyField.set(contractVersionsPayrollPolicies.at(0)?.attributes.payroll_policy_id)
  }

  const recurrenceCountOption = (count: number) => ({
    label: i18n.tp(
      'contract_versions.forms.additional_compensation.modal.every_n_payroll_periods',
      { count }
    ),
    value: count,
  })

  const recurrenceCountOptions: SelectOption[] = [...Array(12).keys()].map((i) =>
    recurrenceCountOption(i + 1)
  )
  const policy = useMemo(() => {
    const policyId =
      typeof policyField.value === 'string' ? parseInt(policyField.value, 10) : policyField.value

    return policies.get(policyId)
  }, [policyField.value]) // eslint-disable-line react-hooks/exhaustive-deps

  const enableDayForPolicy = (day: Date | moment.Moment): boolean => {
    if (!policy) return false

    return enableDay({
      day: day,
      recurrence: policy.attributes.recurrence,
      starts_on: policy.attributes.starts_on,
    })
  }

  return (
    <>
      <Box>
        <Select
          mandatory
          label={i18n.t('contract_versions.forms.additional_compensation.modal.payroll_policy')}
          field={form.get('payroll_policy_id')}
          options={policiesOptions}
          none={i18n.t('common.none')}
          readonly
        />
      </Box>
      <Box>
        <Select
          label={i18n.t('contract_versions.forms.additional_compensation.modal.recurrence_field')}
          field={form.get('recurrence_count')}
          options={recurrenceCountOptions}
          none={i18n.t('common.none')}
          readonly={!isEditable || isPerWorkedDay}
        />
      </Box>
      <Box>
        <StartsOnDatePicker
          form={form}
          label={i18n.t(
            'contract_versions.forms.additional_compensation.modal.first_payment_on_field'
          )}
          readonly={!isEditable}
          enableDay={enableDayForPolicy}
          periodicity={Policy.periodicityKeyByRecurrence(policy!.attributes.recurrence)}
        />
      </Box>
    </>
  )
}

export default observer(SupplementsSettings)
